import cx from 'classnames';
import * as _ from 'lodash';
import { useCallback, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { packageLabelNameRegex } from '../../../../../../shared/packageLabels';
import { Action, type Permission, ResourceType } from '../../../../../../typings/permissions';
import { useDialogContext } from '../../../../../contexts/DialogContext';
import { usePatchPackageLabel } from '../../../../../hooks/api/packageLabels';
import { useHasPermission } from '../../../../../shared/permissions';
import { Badge, DocsLink, Icon, icons } from '../../../../generic';
import Button from '../../../../generic/Button';
import {
  EditableMultiInputContainer,
  type EditableMultiInputItem,
} from '../../../../generic/EditableMultiInput/EditableMultiInputContainer';
import SearchInput from '../../../../generic/Forms/SearchInput';
import { deleteLabelDialogBody, editLabelDialogBody } from '../../../../shared/packageLabels/dialogBodies';
import { LabelTextInput } from '../../../../shared/packageLabels/LabelTextInput';
import type { GeneralSettings } from '../../../../../../shared/settings/general';

import styles from './PackageLabelSelector.module.less';

export type PackageLabelForTable = EditableMultiInputItem & {
  packageCount: number;
};

type AddLabelPanelProps = {
  onAddLabel: (label: string) => void;
  onCancel: () => void;
  isValidLabel: boolean;
  onValidationChange: (isValid: boolean) => void;
};

const AddLabelPanel: VoidFunctionComponent<AddLabelPanelProps> = ({
  onAddLabel,
  onCancel,
  isValidLabel,
  onValidationChange,
}) => {
  const [label, setLabel] = useState('');

  const handleCancel = () => {
    setLabel('');
    onValidationChange(true);
    onCancel();
  };

  return (
    <div className={styles.addLabelPanel}>
      <div className={styles.addLabelPanelInput}>
        <LabelTextInput
          value={label}
          onChange={setLabel}
          inputValidationRegex={packageLabelNameRegex}
          onValidationChange={onValidationChange}
        />
      </div>
      <div className={styles.addLabelPanelButtons}>
        <Button size="small" type="primary" isDisabled={label.length === 0 || !isValidLabel} onClick={() => onAddLabel(label)}>
          Add Label
        </Button>
        <Button size="small" type="text" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

type PackageLabelSelectorProps = {
  settings: GeneralSettings;
  onUpdateSettings: (settings: Partial<GeneralSettings>) => void;
  isDisabled: boolean;
};

/**
 * Displays the Package Labels section of the general settings page.
 * This section allows users to define labels that can be applied to packages.
 */
const PackageLabelSelector: VoidFunctionComponent<PackageLabelSelectorProps> = ({
  settings: { packageLabels },
  onUpdateSettings,
  isDisabled,
}) => {
  const [packageLabelsForTable, setPackageLabelsForTable] = useState<PackageLabelForTable[]>(packageLabels);
  const [shouldShowAddLabelPanel, setShouldShowAddLabelPanel] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { mutate: patchPackageLabel } = usePatchPackageLabel();
  const toggleAddLabelPanel = () => {
    setShouldShowAddLabelPanel(!shouldShowAddLabelPanel);
  };
  const [isValidLabel, setIsValidLabel] = useState(true);

  const { onConfirm } = useDialogContext();

  //  This is the function which actually goes and updates the package labels in useOrganizationSettings.
  const setPackageLabels = useCallback((newLabels: GeneralSettings['packageLabels']) => {
    setPackageLabelsForTable(newLabels);
    return onUpdateSettings({ packageLabels: newLabels });
  }, [onUpdateSettings]);

  const onDeleteConfirmModal = useCallback((deletedItem: PackageLabelForTable, newLabels: PackageLabelForTable[]) => {
    onConfirm({
      title: 'Delete Package Label',
      body: deleteLabelDialogBody(deletedItem),
      onConfirm: () => {
        setPackageLabels(newLabels);
      },
    });
  }, [onConfirm, setPackageLabels]);

  //  This function is called when the user removes one label
  const onDeleteLabels = useCallback(
    (newLabels: PackageLabelForTable[]) => {
      // If this was called without a deletion, return early.
      if (newLabels.length >= packageLabels.length) {
        return;
      }

      //  Find the label that was removed and show a confirmation dialog.
      //  If the user confirms, call the setPackageLabels function to queue the package labels for update in useOrganizationSettings.
      const missingLabel = _.head(_.difference(packageLabels, newLabels));

      // If the label was not found, return early. This should never happen.
      if (!missingLabel) {
        return;
      }

      onDeleteConfirmModal(missingLabel, newLabels);
    },
    [packageLabels, onDeleteConfirmModal],
  );

  const onEditConfirmModal = useCallback(
    (editedItem: PackageLabelForTable, newLabel: string, updatedLabels: PackageLabelForTable[]) => {
      onConfirm({
        title: 'Edit Package Label',
        body: editLabelDialogBody(editedItem, newLabel),
        onConfirm: () => {
          // Send a network request to update the label in the DB without waiting for org settings to be saved.
          // This avoids the updated label being added as a new label when the org settings are saved, as that
          // uses the diff between the DB and the UI to determine if a label was added or removed.
          patchPackageLabel({ labelId: editedItem.id, newLabel });
          // Update the local state to reflect the updated label in the table.
          setPackageLabelsForTable(updatedLabels);
          // Update the package labels in the org settings so that the deletion modal sees the updated label.
          setPackageLabels(updatedLabels);
        },
      });
    },
    [onConfirm, patchPackageLabel, setPackageLabels],
  );

  //  This function is called when the user adds a label.
  const onAddLabel = (label: string) => {
    // null op if the label is empty
    if (label.length === 0) {
      toggleAddLabelPanel();
      return;
    }
    // null op if the label already exists
    if (packageLabelsForTable.find(({ name }) => name === label)) {
      toggleAddLabelPanel();
      return;
    }
    // we can use any value for the ID and packageCount, as they are not used until the label is saved.
    // at which point the ID will be set and the packageCount will be updated.
    const newLabels = [{ name: label, id: 0, packageCount: 0, isNew: true }, ...packageLabelsForTable];
    setPackageLabels(newLabels);
    toggleAddLabelPanel();
  };

  const hasPermission: (permission: Permission) => boolean = useHasPermission();

  const hasEditPermission = hasPermission({
    resourceType: ResourceType.PackageLabel,
    action: Action.Create,
  });

  return (
    <div>
      <div>
        <p className={styles.packageLabelsTitle}>Package Labels</p>
        <p className={styles.packageLabelsDescription}>
          These labels can be assigned to packages and are visible to your entire organization. Learn more about using
          labels in our <DocsLink to="using-package-labels">docs</DocsLink>.
          <br />
          <p
            className={cx({
              [styles.packageLabelsDescriptionInvalidLabel]: !isValidLabel,
              [styles.packageLabelsDescription]: isValidLabel,
            })}
          >
            Label names must be 1-50 characters long and contain only the characters a-z, A-Z, 0-9, underscores,
            hyphens, periods, or spaces.
          </p>
        </p>
      </div>
      <div className={styles.packageLabelsSearchContainer}>
        <SearchInput
          label="package labels search"
          placeholder="Search..."
          value={searchTerm}
          onChange={(term: string) => setSearchTerm(term)}
          size="small"
          isDisabled={shouldShowAddLabelPanel}
        />
      </div>
      <div className={styles.packageLabelsHeader}>
        {shouldShowAddLabelPanel && !isDisabled ? (
          <AddLabelPanel
            onAddLabel={onAddLabel}
            onCancel={toggleAddLabelPanel}
            isValidLabel={isValidLabel}
            onValidationChange={setIsValidLabel}
          />
        ) : (
          <Button size="medium" type="text" onClick={toggleAddLabelPanel}>
            <Icon icon={icons.plusCircleO} />
            <span className={styles.addLabelButtonText}>Add Label</span>
          </Button>
        )}
        <Badge color="light-gray">{packageLabelsForTable.length}/500</Badge>
      </div>
      <EditableMultiInputContainer<PackageLabelForTable>
        items={packageLabelsForTable}
        onItemDelete={onDeleteLabels}
        onItemEdit={(editedItem, newLabel, updatedLabels) => {
          onEditConfirmModal(editedItem, newLabel, updatedLabels);
        }}
        hasEditPermission={hasEditPermission}
        maxItemNameLength={50}
        searchTerm={searchTerm}
        initialLoadSize={10}
        inputValidationRegex={packageLabelNameRegex}
        onValidationChange={setIsValidLabel}
      />
    </div>
  );
};

export default PackageLabelSelector;
